// client/src/components/pages/LexiStar/Routes/LaneList.jsx
//
// Left column of Routes & lanes (LS-09 §5.11): directional lanes (A → B and
// B → A are different lanes), searchable, each with its route count and how
// the organisation sees it (Owner / Dispatcher / Receiver). Plus the "New lane"
// dialog: two sites from the location master data.

import React, { useMemo, useState } from 'react';
import { FaSearch, FaArrowRight, FaTimes, FaRoute } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';

const ACCESS_STYLES = {
  Owner: 'bg-primary-100 text-primary-800',
  Dispatcher: 'bg-blue-50 text-blue-700',
  Receiver: 'bg-green-50 text-green-700',
  Admin: 'bg-slate-100 text-slate-600',
};

export const laneLabel = (lane) =>
  `${lane.from_location_name || `Site ${lane.from_location_id}`} → ${
    lane.to_location_name || `Site ${lane.to_location_id}`
  }`;

export default function LaneList({ lanes, loading, selectedId, onSelect }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lanes;
    return lanes.filter((l) =>
      [l.name, l.from_location_name, l.to_location_name]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [lanes, search]);

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-900">Lanes</h2>
          <span className="text-xs text-slate-500 tabular-nums">{lanes.length}</span>
        </div>
        <div className="relative">
          <FaSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={12}
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a site…"
            aria-label="Search lanes"
            className="pl-8 pr-3 py-1.5 w-full border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <ul className="overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-15rem)] divide-y divide-slate-100">
        {loading && lanes.length === 0 && (
          <li className="px-4 py-6 text-sm text-slate-500">Loading lanes…</li>
        )}
        {!loading && filtered.length === 0 && (
          <li className="px-4 py-8 text-center">
            <FaRoute className="mx-auto text-slate-300 mb-2" size={22} aria-hidden="true" />
            <p className="text-sm text-slate-600">
              {lanes.length === 0
                ? 'No lanes yet. A lane is one direction between two sites; its routes are the ways trucks may drive it.'
                : 'No lane matches that search.'}
            </p>
          </li>
        )}
        {filtered.map((lane) => {
          const selected = lane.id === selectedId;
          return (
            <li key={lane.id}>
              <button
                type="button"
                onClick={() => onSelect(lane)}
                aria-current={selected ? 'true' : undefined}
                className={`w-full text-left px-4 py-3 transition-colors ${
                  selected ? 'bg-primary-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  <span className="truncate">
                    {lane.from_location_name || lane.from_location_id}
                  </span>
                  <FaArrowRight
                    className="flex-shrink-0 text-slate-400"
                    size={10}
                    aria-hidden="true"
                  />
                  <span className="truncate">{lane.to_location_name || lane.to_location_id}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                  <span className="tabular-nums">
                    {lane.route_count} route{lane.route_count === 1 ? '' : 's'}
                  </span>
                  {lane.access_type && lane.access_type !== 'Owner' && (
                    <span
                      className={`px-1.5 py-px rounded-full ${ACCESS_STYLES[lane.access_type] || ACCESS_STYLES.Admin}`}
                    >
                      {lane.access_type === 'Admin' ? 'Admin view' : lane.access_type}
                    </span>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** New lane: pick the source and destination sites. */
export function NewLaneDialog({ isOpen, sites, onClose, onCreate }) {
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!fromId || !toId) return setError('Choose both sites.');
    if (fromId === toId) return setError('A lane needs two different sites.');
    setSaving(true);
    try {
      await onCreate({
        from_location_id: Number(fromId),
        to_location_id: Number(toId),
        name: name.trim() || undefined,
      });
      setFromId('');
      setToId('');
      setName('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create the lane.');
    } finally {
      setSaving(false);
    }
  };

  const siteOptions = sites.map((s) => (
    <option key={s.location_id} value={String(s.location_id)}>
      {s.location_name}
      {s.location_code ? ` (${s.location_code})` : ''}
    </option>
  ));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="New lane"
        onSubmit={submit}
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-bold text-slate-900">New lane</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-600">
            Lanes are directional: the way back is its own lane with its own routes.
          </p>
          <div>
            <label htmlFor="lane-from" className="block text-sm font-medium text-slate-700 mb-1">
              From (source site)
            </label>
            <ThemedSelect id="lane-from" value={fromId} onChange={(e) => setFromId(e.target.value)}>
              <option value="">Choose a site…</option>
              {siteOptions}
            </ThemedSelect>
          </div>
          <div>
            <label htmlFor="lane-to" className="block text-sm font-medium text-slate-700 mb-1">
              To (destination site)
            </label>
            <ThemedSelect id="lane-to" value={toId} onChange={(e) => setToId(e.target.value)}>
              <option value="">Choose a site…</option>
              {siteOptions}
            </ThemedSelect>
          </div>
          <div>
            <label htmlFor="lane-name" className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              id="lane-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              placeholder="Defaults to “From → To”"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          {sites.length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No active sites found. Sites come from Order Management → Master Data → Locations.
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-60"
          >
            {saving ? 'Creating…' : 'Create lane'}
          </button>
        </div>
      </form>
    </div>
  );
}
